import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  PageWrapper,
  Title,
  Note,
  Table,
  Row,
  HeaderRow,
  Cell,
  StatusPaid,
  StatusUnpaid,
  PayButton,
  DownloadButton,
  PaginationWrapper,
  NavButton,
  PageButton,
  SearchInput,
  FilterBar,
  Select,
  TableWrapper,
  DeleteButton,
  ButtonTranslateFileModal,
} from "../../style/TransactionHistory_Style";
import PaymentService from "../../services/api/payment.service";
import {
  EQrcodeRequest,
  IGetPageHistoryTransactionByUser,
} from "../../models/payment";
import { toast } from "../../utils/notify";
import { EPaymentStatus, HistoryTranslate, UploadFile } from "./AiToolsPage";
import { useTranslation } from "react-i18next";
import {
  ButtonCloseModal,
  ButtonPaymentModal,
  ButtonSaveFileModal,
  CloseButton,
  FileName,
  FileSize,
  PreviewCanvas,
  PreviewContent,
  PreviewFooter,
  PreviewHeader,
  PreviewModal,
  PreviewOverlay,
  PreviewOverlayPreview,
  PreviewTitle,
  ProcessText,
  Spinner,
  ProcessModal,
  ProcessFileCard,
  ProcessFileExt,
  ProcessLoader,
  ProcessTip,
} from "../../style/AIToolsPage_Style";
import { Box } from "@mui/material";
import TranslateService from "../../services/api/translate.service";
import {
  connectSignal,
  getSignalConnection,
} from "../../services/api/signalr.Service";
import pdfjsLib from "../../pdfConfig";
import { envConfig } from "../../config/env.config";
import { PayPalButtons } from "@paypal/react-paypal-js";

export interface TransactionItem {
  id: string;
  fileName: string;
  fileSize: number; // bytes
  fromLang: string;
  toLang: string;
  isPaid: boolean;
}

const formatFileSize = (bytes: number) => {
  return bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(1)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const TransactionHistory: React.FC = () => {
  const PAGE_SIZE = 10;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<EPaymentStatus | "all">("all");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showNotiteDeleteFileModal, setShowNotiteDeleteFileModal] =
    useState(false);
  const [fileId, setFileId] = useState<string>();
  const [totalPages, setTotalPages] = useState(0);
  const { t } = useTranslation();
  const [historyTranslate, setHistoryTranslate] = useState<HistoryTranslate>();
  const [historyTranslates, setHistoryTranslates] =
    useState<HistoryTranslate[]>();
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFile, setPreviewFile] = useState<UploadFile | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [isRenderingPreview, setIsRenderingPreview] = useState(false);

  useEffect(() => {
    GetPageHistoryTransactionByUser();
  }, [page, search, status]);

  useEffect(() => {
    setPage(1);
  }, [search, status]);

  useEffect(() => {
    const initSignalR = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      const conn = await connectSignal(token);

      conn.off("TranslateCompleted");
      conn.off("TranslateFailed");

      conn.on("TranslateCompleted", async (data: HistoryTranslate) => {
        try {
          const filePreview = await TranslateService.getFilePreview(
            data.orderId,
          );

          setPreviewFile({
            id: filePreview.data.id,
            file: new File([], filePreview.data.fileName),
            status: filePreview.data.status,
            progress: 0,
            isTranslating: false,
            orderId: filePreview.data.orderId,
            price: filePreview.data.price,
            previewBase64: buildPreviewImageUrl(
              filePreview.data.filePreviewBase64,
            ),
            fileBase64: filePreview.data.fileDownloadBase64,
            fileName: filePreview.data.fileName,
            numberOfTranslationServicesUsed:
              filePreview.data.numberOfTranslationServicesUsed,
            totalNumberOfTranslationServicesUsed:
              filePreview.data.totalNumberOfTranslationServicesUsed,
          });

          setShowProcessModal(false);
          setShowPreviewModal(true);
        } catch (error) {
          toast(t("error.translate_fail"), "error");
          setShowProcessModal(false);
        } finally {
          setIsTranslating(false);
          setFiles((prev) =>
            prev.map((f) => ({ ...f, isTranslating: false, progress: 0 })),
          );

          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
      });

      conn.on("TranslateFailed", () => {
        toast(t("error.translate_fail"), "error");
        setShowProcessModal(false);
        setIsTranslating(false);
        setFiles([]);
      });
    };

    initSignalR();

    return () => {
      const conn = getSignalConnection();
      conn?.off("TranslateCompleted");
      conn?.off("TranslateFailed");
    };
  }, []);

  const GetPageHistoryTransactionByUser = useCallback(async () => {
    const params: IGetPageHistoryTransactionByUser = {
      CurrentPage: page,
      PageSize: PAGE_SIZE,
      KeySearch: search,
      Status: status === "all" ? undefined : status,
    };

    const res = await PaymentService.GetPageHistoryTransactionByUser(params);

    if (res.success) {
      setHistoryTranslates(res.items);
      setTotalPages(res.totalPages);
    } else {
      toast(res.message, "error");
    }
  }, [page, search, status]);

  const buildPreviewImageUrl = (path?: string) => {
    if (!path) return "";

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const baseUrl = envConfig.API_ENDPOINT.replace(/\/api\/?$/i, "").replace(
      /\/$/g,
      "",
    );

    const cleanPath = path.startsWith("/") ? path : `/${path}`;

    return encodeURI(`${baseUrl}${cleanPath}`);
  };

  const handleGetQrcode = async (orderId: string, price: number) => {
    const data: EQrcodeRequest = {
      orderId: orderId,
      price: price, //previewFile.price ?? 0,
    };

    const qrcode = await PaymentService.GetQrCode(data);

    if (qrcode.success) {
      window.open(qrcode.data, "_blank");
    } else {
      toast(t("transaction.getQrFail"), "error");
    }
  };

  const formatVND = (value: number): string => {
    return new Intl.NumberFormat("vi-VN").format(value);
  };

  const formatDate = (date?: string | Date): string => {
    if (!date) return "";

    const d = new Date(date);

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  };

  // const createOrder = async (
  //   customOrderId: string | undefined,
  //   price?: number,
  // ) => {
  //   const res = await fetch(
  //     envConfig.API_ENDPOINT +
  //       `/paypal/create-order?customOrderId=${customOrderId}&price=${price}`,
  //     {
  //       method: "POST",
  //     },
  //   );

  //   if (!res.ok) {
  //     toast("Tạo thanh toán thất bại", "error");
  //     return;
  //   }

  //   const data = await res.json();

  //   return data.id;
  // };

  // const onApprove = async (data: any, customOrderId?: string) => {
  //   await fetch(
  //     envConfig.API_ENDPOINT +
  //       `/paypal/capture-order?orderId=${data.orderID}&customOrderId=${customOrderId}`,
  //     { method: "POST" },
  //   );

  //   toast("Thanh toán PayPal thành công", "success");

  //   setShowPaymentModal(false);

  //   window.open(
  //     "https://docme.vn/transaction?orderId=" + customOrderId,
  //     "_blank",
  //   );
  // };

  const getPagination = (current: number, total: number) => {
    const delta = 2; // số page xung quanh current
    const range: (number | string)[] = [];

    const left = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);

    // luôn có trang 1
    range.push(1);

    if (left > 2) {
      range.push("...");
    }

    for (let i = left; i <= right; i++) {
      range.push(i);
    }

    if (right < total - 1) {
      range.push("...");
    }

    // luôn có trang cuối
    if (total > 1) {
      range.push(total);
    }

    return range;
  };

  const deleteFile = async (id: string) => {
    const res = await TranslateService.deleteFile(id);

    if (res.success) {
      setHistoryTranslates((prev) =>
        prev?.map((item) =>
          item.id === id
            ? {
                ...item,
                status: EPaymentStatus.delete,
              }
            : item,
        ),
      );

      toast(res.message, "success");
    } else {
      toast(res.message, "error");
    }
  };

  const GetFileHistoryTranslate = async (id: string) => {
    const res = await PaymentService.GetFileHistoryTranslate(id);

    if (res.success) {
      await downloadUrlFile(res.data.fileDownloadBase64, res.data.fileName);
      toast(res.message, "success");
    } else {
      toast(res.message, "error");
    }
  };

  const buildFileUrl = (path?: string) => {
    if (!path) return "";

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const baseUrl = envConfig.API_ENDPOINT.replace(/\/api\/?$/i, "").replace(
      /\/$/g,
      "",
    );

    const cleanPath = path.startsWith("/") ? path : `/${path}`;

    return encodeURI(`${baseUrl}${cleanPath}`);
  };

  const downloadUrlFile = async (fileUrl: string, fileName?: string) => {
    const finalUrl = buildFileUrl(fileUrl);

    if (!finalUrl) {
      toast("Không tìm thấy đường dẫn file", "error");
      return;
    }

    try {
      const response = await fetch(finalUrl);

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName || "translated-file";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      // Fallback nếu bị CORS hoặc server không cho fetch trực tiếp
      window.open(finalUrl, "_blank");
    }
  };

  const handleDownloadPreviewFile = async (id: string) => {
    if (downloadingId) return;

    try {
      setDownloadingId(id);

      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
      });

      await GetFileHistoryTranslate(id);

      await new Promise((resolve) => setTimeout(resolve, 600));
    } catch (error) {
      toast("Tải file thất bại", "error");
    } finally {
      setDownloadingId(null);
    }
  };

  const getProcessText = () => t("translate.ai_processing");

  const handleCreate = async (historyTranslate: HistoryTranslate) => {
    setFiles([
      {
        id: crypto.randomUUID(),
        fileName: historyTranslate.fileName,
        fileSize: historyTranslate.fileSize,
        status: EPaymentStatus.pending,
        progress: 0,
        isTranslating: true,
        orderId: historyTranslate.orderId,
      },
    ]);

    setShowProcessModal(true);
    setIsTranslating(true);

    try {
      await TranslateService.createSuccess(historyTranslate);
    } catch (error) {
      toast(t("error.translate_fail"), "error");
      setShowProcessModal(false);
      setIsTranslating(false);
      setFiles([]);
    }
  };

  const createOrder = async (customOrderId?: string | null, price?: number) => {
    const res = await fetch(
      envConfig.API_ENDPOINT +
        `/paypal/create-order?customOrderId=${customOrderId}&price=${price}`,
      {
        method: "POST",
      },
    );

    if (!res.ok) {
      toast("Tạo thanh toán thất bại", "error");
      return;
    }

    const data = await res.json();
    return data.id;
  };

  const onApprove = async (data: any, customOrderId?: string) => {
    await fetch(
      envConfig.API_ENDPOINT +
        `/paypal/capture-order?orderId=${data.orderID}&customOrderId=${customOrderId}`,
      { method: "POST" },
    );

    toast("Thanh toán PayPal thành công", "success");

    setShowPaymentModal(false);

    // cập nhật trạng thái file đã thanh toán
    setFiles((prev) =>
      prev.map((f) =>
        f.id === previewFile?.id ? { ...f, status: EPaymentStatus.success } : f,
      ),
    );
    envConfig.KEY_BASE === "vn"
      ? window.open(
          "https://docme.vn/transaction?orderId=" + customOrderId,
          "_blank",
        )
      : window.open(
          "https://docme.world/transaction?orderId=" + customOrderId,
          "_blank",
        );
  };

  const processFile = files.find((f) => f.isTranslating) || files[0];

  const processFileName =
    processFile?.file?.name || processFile?.fileName || "Document";

  const processFileSize = processFile?.file?.size
    ? formatFileSize(processFile.file.size)
    : processFile?.fileSize
      ? formatFileSize(processFile.fileSize)
      : "";

  const processFileExt =
    processFileName.split(".").pop()?.toUpperCase() || "FILE";

  return (
    <PageWrapper>
      <Title>{t("transaction.title")}</Title>

      <Note>
        {t("transaction.note")}
        <Box style={{ color: "red", fontSize: "14px", paddingTop: "5px" }}>
          <b>
            <i> {t("translation.fileRetentionNotice")}</i>
          </b>
        </Box>
      </Note>

      <FilterBar>
        <SearchInput
          placeholder={t("transaction.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as EPaymentStatus | "all")}>
          <option value="all">{t("transaction.status.all")}</option>
          <option value={EPaymentStatus.success}>
            {t("transaction.status.paid")}
          </option>
          <option value={EPaymentStatus.pending}>
            {t("transaction.status.unpaid")}
          </option>
        </Select>
      </FilterBar>
      <TableWrapper>
        <Table>
          <HeaderRow>
            <Cell>{t("transaction.table.fileName")}</Cell>
            <Cell>{t("transaction.table.fileSize")}</Cell>
            <Cell>{t("transaction.table.language")}</Cell>
            <Cell>{t("transaction.table.status")}</Cell>
            <Cell>{t("transaction.table.price")}</Cell>
            <Cell>{t("transaction.table.expirationDate")}</Cell>
            <Cell>{t("transaction.table.action")}</Cell>
          </HeaderRow>

          {historyTranslates &&
            historyTranslates.map((item) => (
              <Row key={item.id}>
                <Cell>{item.fileName}</Cell>
                <Cell>{formatFileSize(item.fileSize)}</Cell>
                <Cell>
                  {item.from} → {item.to}
                </Cell>
                <Cell>
                  {item.status !== EPaymentStatus.delete ? (
                    item.status === EPaymentStatus.success ||
                    item.status === EPaymentStatus.translate_Completed ||
                    item.status === EPaymentStatus.translate_Processing ? (
                      <StatusPaid>{t("transaction.status.paid")}</StatusPaid>
                    ) : (
                      <StatusUnpaid>
                        {t("transaction.status.unpaid")}
                      </StatusUnpaid>
                    )
                  ) : (
                    t("transaction.fileDeleted")
                  )}
                </Cell>
                <Cell>
                  {" "}
                  {envConfig.KEY_BASE === "vn"
                    ? item?.price && formatVND(item?.price) + " VNĐ"
                    : item?.price && item?.price + " $"}
                </Cell>
                <Cell>
                  {item?.expirationDate && formatDate(item?.expirationDate)}
                </Cell>
                <Cell>
                  {item.status !== EPaymentStatus.delete ? (
                    item.status === EPaymentStatus.pending ? (
                      <PayButton
                        onClick={() => {
                          setHistoryTranslate(item);
                          setShowPaymentModal(true);
                        }}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          strokeWidth="0.8"
                          className="bi bi-credit-card"
                          viewBox="0 0 16 16">
                          <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v1h14V4a1 1 0 0 0-1-1zm13 4H1v5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1z" />
                          <path d="M2 10a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" />
                        </svg>
                        {t("transaction.pay")}
                      </PayButton>
                    ) : item.status === EPaymentStatus.translate_Completed ? (
                      <Box sx={{ display: "flex", gap: 2 }}>
                        {downloadingId === item.id ? (
                          <>
                            <Spinner
                              style={{
                                width: 16,
                                height: 16,
                                borderWidth: 2,
                                borderColor: "rgba(111,69,114,0.25)",
                                borderTopColor: "#6f4572",
                              }}
                            />
                            {t("common.loading")}...
                          </>
                        ) : (
                          <>
                            <DownloadButton
                              onClick={() =>
                                handleDownloadPreviewFile(item.id)
                              }>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 16 16"
                                fill="currentColor"
                                stroke="currentColor"
                                strokeWidth="0.8"
                                className="bi bi-download">
                                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                              </svg>
                              {t("transaction.download")}
                            </DownloadButton>

                            <DeleteButton
                              onClick={() => {
                                if (item.id) {
                                  setFileId(item.id);
                                  setShowNotiteDeleteFileModal(true);
                                }
                              }}>
                              {t("transaction.delete")}
                            </DeleteButton>
                          </>
                        )}
                      </Box>
                    ) : item?.status === EPaymentStatus.translate_Processing ? (
                      <ButtonTranslateFileModal
                        // onClick={() =>
                        //   historyTranslate && handleCreate(historyTranslate)
                        // }
                        disabled>
                        <>{t("file.translating_short_complete")}</>
                      </ButtonTranslateFileModal>
                    ) : (
                      item.status === EPaymentStatus.success && (
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <ButtonTranslateFileModal
                            onClick={() => handleCreate(item)}
                            disabled={isTranslating}>
                            <>
                              <img
                                style={{ filter: "invert(1)" }}
                                width={"20px"}
                                src="/icon/ai-file_9812085.png"
                              />{" "}
                              {t("file.translate_now")}
                            </>
                          </ButtonTranslateFileModal>
                        </Box>
                      )
                    )
                  ) : (
                    t("transaction.fileDeleted")
                  )}
                </Cell>
              </Row>
            ))}
        </Table>
      </TableWrapper>

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationWrapper>
          <NavButton disabled={page === 1} onClick={() => setPage(page - 1)}>
            {t("pagination.prev")}
          </NavButton>

          {getPagination(page, totalPages).map((item, i) =>
            item === "..." ? (
              <span key={i} style={{ padding: "0 8px" }}>
                ...
              </span>
            ) : (
              <PageButton
                key={i}
                active={page === item}
                onClick={() => setPage(item as number)}>
                {item}
              </PageButton>
            ),
          )}

          <NavButton
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}>
            {t("pagination.next")}
          </NavButton>
        </PaginationWrapper>
      )}

      {showPreviewModal && (
        <PreviewOverlayPreview>
          <PreviewModal>
            <PreviewHeader>
              <PreviewTitle>{t("preview.title")}</PreviewTitle>
              <CloseButton onClick={() => setShowPreviewModal(false)}>
                ✕
              </CloseButton>
            </PreviewHeader>

            <PreviewContent
              style={{
                position: "relative",
                minHeight: 500,
              }}>
              {isRenderingPreview && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    background: "rgba(255,255,255,.92)",
                    backdropFilter: "blur(6px)",
                    zIndex: 999,
                    gap: 24,
                  }}>
                  <div
                    style={{
                      width: 70,
                      height: 70,
                      borderRadius: "50%",
                      border: "5px solid #dbeafe",
                      borderTop: "5px solid #2563eb",
                      animation: "spin 1s linear infinite",
                    }}
                  />

                  <div
                    style={{
                      width: 260,
                      height: 6,
                      background: "#e5e7eb",
                      borderRadius: 999,
                      overflow: "hidden",
                    }}>
                    <div
                      style={{
                        width: "40%",
                        height: "100%",
                        borderRadius: 999,
                        background:
                          "linear-gradient(90deg,#2563eb,#60a5fa,#2563eb)",
                        animation: "loadingBar 1.2s infinite ease-in-out",
                      }}
                    />
                  </div>

                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 600,
                      color: "#1e3a8a",
                    }}>
                    {t("preview.loading_title")}
                    <span className="loading-dots">...</span>
                  </div>

                  <div
                    style={{
                      color: "#6b7280",
                      fontSize: 14,
                    }}>
                    {t("preview.loading_desc")}
                  </div>
                </div>
              )}

              <div id="pdf-preview-container">
                {previewFile?.previewBase64 && (
                  <img
                    src={previewFile.previewBase64}
                    alt={previewFile.fileName || "Preview"}
                    onLoad={() => setIsRenderingPreview(false)}
                    onError={() => {
                      setIsRenderingPreview(false);
                      toast(
                        t("error.preview_fail") || t("error.translate_fail"),
                        "error",
                      );
                    }}
                    style={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                      borderRadius: 12,
                      background: "#ffffff",
                    }}
                  />
                )}
              </div>
            </PreviewContent>

            <PreviewFooter>
              <i className="footer-item-i">
                <img
                  style={{
                    filter:
                      "invert(17%) sepia(96%) saturate(7492%) hue-rotate(358deg) brightness(95%) contrast(112%)",
                  }}
                  width={30}
                  src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAACXBIWXMAAAsTAAALEwEAmpwYAAADLUlEQVR4nO2YW4hNURjHf2MuucygiJmU5JIXFMIULxMPGl7IpcjgxVAYl7yIBxQKD3gYPLhElIRccsklNSGM3MaDW8gYd7mEGWO2Vv67lm3O6Zyz9z7nqP2r1Tn722t93/r2+tZa31oQERERERERERhtgErgNtAIOCGXJqAOWAzkBecG7ElD550Y5USqzhQBm4DHwFPgghR+BSqAQsKnEJgNfJHtpckqGAA8iPFl5pJ+Zsr2/WQajQI+qOEtYDBQAjRLZkYq3bSX7Z+JVM7R0DWp0XEpcHGypMSlizpuKrYAm1uZWFnvyHDgiSp9AqbEqJe1juQAVVYoXQd6x3E4Kx0pAc5aobQRyI83bInGaIg4XvvjgTcSvtVzSorSjOPabwts1QgYwRmNTNKK4pALlAFbFKr3gJ1AP38+/G3/pv58BxZpjhCQI/nagWNtombk+yRpL6b9Bm1wA0mN1hwpUkL3zHpvUprVQKlG4ojk+1K0+4/9fGWvBOBIMbDWygAcZakzWtl/+uq9qesHJ6g56iraAfywni8B4+KEamfVawrIvi+GeWL+F3AIGJFA2wq1uZNJR3KAY5aSb0C1wiURzAR/obYLyaAjecAr4D2wBuiWRNuRQL2MXwnghOf4Da0OQEES9XvqJOnuV9eArn46wJ+P4IZ06AwBdlm5m/ldB7QLQHepdJplPjTGArXW0Ddrz+gfgO5cLSh10r2BkJhsjUDY5S7QMQwnpuvoGWbnW4DnugTpFIYTlZp4xtgqYJb+H/DUq5Z8gecg5ygvyxgmxVlurUhmSTas0PN6T/2TkpdbsiWSmQ017ZgjwCTgqrUMmtOly37J53na1Ug+2lpGTaw7ypZ9dai7stKhwBhgopSajq3UyrAdOAic0vWody5clL4CJY+u3CSNNuesThu72/Rc72dZvuxj0jWq81W6aTSyl8BnT729SmtMmW+FoONJc8r8jEaNOmSOuY+AG8B54DCwW1dAJuaXAXOAqToGD9JIukwD3lmrSq1uAl0HG3TF6r4/DbwGPgJHpS9rMBtVL6XoLuX6QO6XfwhM4D+mGOjh8xAXERERERERQRx+A1Ns/jikzsHQAAAAAElFTkSuQmCC"
                  alt="card-in-use"></img>{" "}
                {t("preview.note")}
              </i>

              <div className="footer-item-div">
                <ButtonSaveFileModal
                  onClick={() =>
                    previewFile?.status ===
                      EPaymentStatus.translate_Completed &&
                    handleDownloadPreviewFile(previewFile.id)
                  }>
                  {downloadingId ? (
                    <>
                      <Spinner
                        style={{
                          width: 16,
                          height: 16,
                          borderWidth: 2,
                          borderColor: "rgba(111,69,114,0.25)",
                          borderTopColor: "#6f4572",
                        }}
                      />
                      {t("common.loading")}...
                    </>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        className="bi bi-download">
                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                        <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                      </svg>
                      {t("preview.download")}
                    </>
                  )}
                </ButtonSaveFileModal>

                <ButtonCloseModal onClick={() => setShowPreviewModal(false)}>
                  {t("common.close")}
                </ButtonCloseModal>
              </div>
            </PreviewFooter>
          </PreviewModal>
        </PreviewOverlayPreview>
      )}

      {showPaymentModal && (
        <PreviewOverlay>
          <PreviewModal style={{ maxWidth: 420 }}>
            <PreviewHeader>
              <PreviewTitle>{t("preview.choose_payment_method")}</PreviewTitle>
              <CloseButton onClick={() => setShowPaymentModal(false)}>
                ✕
              </CloseButton>
            </PreviewHeader>

            <PreviewContent
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
                padding: 20,
              }}>
              {envConfig.KEY_BASE === "vn" ? (
                <>
                  {/* VietQR */}
                  <ButtonPaymentModal
                    sx={{ justifyContent: "center" }}
                    onClick={() => {
                      historyTranslate &&
                        handleGetQrcode(
                          historyTranslate.orderId,
                          historyTranslate.price,
                        );
                    }}>
                    <img
                      style={{
                        height: "20px",
                        filter: "drop-shadow(0px 1px 1px white)",
                      }}
                      src="/icon/VietQR.46a78cbb.png"
                    />{" "}
                    🇻🇳 VietQR
                  </ButtonPaymentModal>
                </>
              ) : (
                <>
                  {/* PayPal */}
                  <div style={{ marginTop: 10 }}>
                    <PayPalButtons
                      style={{ layout: "vertical" }}
                      createOrder={() =>
                        createOrder(
                          historyTranslate?.orderId,
                          historyTranslate?.price,
                        )
                      }
                      onApprove={(data) =>
                        onApprove(data, historyTranslate?.orderId)
                      }
                    />
                  </div>
                </>
              )}
            </PreviewContent>
          </PreviewModal>
        </PreviewOverlay>
      )}

      {showProcessModal && processFile && (
        <PreviewOverlay
          style={{
            background: "rgba(15, 23, 42, 0.62)",
            backdropFilter: "blur(8px)",
          }}>
          <ProcessModal>
            <PreviewHeader
              style={{
                borderBottom: "none",
                paddingBottom: 0,
                justifyContent: "center",
              }}>
              <PreviewTitle
                style={{
                  textAlign: "center",
                  fontSize: 20,
                  fontWeight: 800,
                }}>
                {t("file.translating_short_complete")}
              </PreviewTitle>
            </PreviewHeader>

            <PreviewContent
              style={{
                marginTop: 18,
                padding: 0,
                background: "transparent",
                maxHeight: "unset",
                overflow: "visible",
                textAlign: "center",
              }}>
              <ProcessFileCard>
                <ProcessFileExt>{processFileExt}</ProcessFileExt>

                <div style={{ minWidth: 0, textAlign: "left" }}>
                  <FileName style={{ maxWidth: 330 }}>
                    {processFileName}
                  </FileName>

                  <FileSize>
                    {processFileSize}
                    {processFileSize ? " · " : ""}
                    Đang xử lý
                  </FileSize>
                </div>
              </ProcessFileCard>

              <ProcessLoader aria-hidden="true">
                <span />
                <span />
                <span />
                <i />
              </ProcessLoader>

              <ProcessText>{getProcessText()}</ProcessText>

              <ProcessTip>{t("translation.keepThisPageOpen")}</ProcessTip>

              <ProcessTip>{t("translation.emailNotice")}</ProcessTip>
            </PreviewContent>
          </ProcessModal>
        </PreviewOverlay>
      )}

      {showNotiteDeleteFileModal && (
        <PreviewOverlay>
          <PreviewModal style={{ maxWidth: 420 }}>
            <PreviewContent
              style={{
                background: "#ffffff",
                display: "flex",
                flexDirection: "column",
                gap: 16,
                padding: 20,
                textAlign: "center",
              }}>
              <div
                style={{
                  width: "auto",
                  textAlign: "center",
                }}>
                <img src="/icon/icons8-warning.gif" />
              </div>
              {t("transaction.notiteDeleteFile")}
            </PreviewContent>
            <PreviewFooter
              style={{ justifyContent: "center", alignItems: "center" }}>
              <div style={{ display: "flex", gap: 6 }}>
                <ButtonPaymentModal
                  onClick={() => {
                    if (fileId) {
                      deleteFile(fileId);
                      setShowNotiteDeleteFileModal(false);
                    }
                  }}>
                  {t("common.ok")}
                </ButtonPaymentModal>

                <ButtonCloseModal
                  onClick={() => setShowNotiteDeleteFileModal(false)}>
                  {t("common.close")}
                </ButtonCloseModal>
              </div>
            </PreviewFooter>
          </PreviewModal>
        </PreviewOverlay>
      )}
    </PageWrapper>
  );
};
